/**
 * Keep the tenant store fed from `/v1/me/tenants`.
 *
 * The last missing link in F-024. The store has always had `setTenants` and the
 * switcher has always read from it; nothing ever called it, so the list stayed
 * empty and the switcher — which self-hides on a list of one or fewer — was
 * permanently invisible. Its own TODO asked for precisely this hook.
 *
 * Mounted once in the app shell. Cheap: one cached read per session.
 */

import { useEffect } from 'react';
import { useMyTenants } from './api/me-tenants';
import { useTenantStore } from './tenant-store';

export function useSyncMyTenants(enabled = true): void {
  const { data } = useMyTenants(enabled);
  const setTenants = useTenantStore((s) => s.setTenants);

  useEffect(() => {
    if (!data?.tenants) return;
    setTenants(
      data.tenants.map((t) => ({
        id: t.tenantId,
        // The store's `Tenant` shape predates this endpoint and carries a slug
        // the API does not return. Nothing reads it — the switcher renders
        // `name` — so mirror the id rather than inventing one.
        slug: t.tenantId,
        name: t.tenantName ?? 'Workspace',
      })),
    );

    // Adopt the token's own workspace as active. The store persists
    // `activeTenantId` across sessions, and a stale one would leave the
    // switcher highlighting a workspace the live token is not scoped to —
    // which reads as a successful switch that never happened.
    const current = data.tenants.find((t) => t.current);
    if (current) {
      const st = useTenantStore.getState();
      if (st.activeTenantId !== current.tenantId) {
        useTenantStore.setState({ activeTenantId: current.tenantId });
      }
    }
  }, [data, setTenants]);
}
